import type React from "react";
import { useEffect, useState, useRef } from "react";
import MobileBanner from "./components/MobileBanner";
import LeftSidebar from "./components/LeftSidebar";
import MediaPlayer, { type MediaPlayerRef } from "./components/MediaPlayer";
import PreviewArea from "./components/PreviewArea";
import RightSidebar from "./components/RightSidebar";
import TopBar from "./components/TopBar";
import { parseLrcFile, type TimedLine } from "./utils/lyricsParser";
import { allPokemon, allShinyPokemon } from "./data/pokemonList";
import { matchesPokemonSearch } from "./utils/pokemonFormatter";

function App(): React.JSX.Element {
  const [mappings, setMappings] = useState<Map<string, string>>(new Map());
  const [selectedPokemon, setSelectedPokemon] = useState<string | null>(null);
  const [currentLyrics, setCurrentLyrics] = useState<string>("");
  const [spriteSize, setSpriteSize] = useState<number>(64);
  const [hidePokeballs, setHidePokeballs] = useState<boolean>(false);
  const [showRed, setShowRed] = useState<boolean>(true);
  const [pokemonSearch, setPokemonSearch] = useState<string>("");
  const [timedLines, setTimedLines] = useState<TimedLine[]>([]);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [textInput, setTextInput] = useState<string>("");
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLooping, setIsLooping] = useState<boolean>(false);
  const [revealAllPokemon, setRevealAllPokemon] = useState<boolean>(false);
  const [showShiny, setShowShiny] = useState<boolean>(false);
  
  const handleSetTextInput = (text: string): void => {
    console.log('Setting textInput to:', text);
    setTextInput(text);
  };

  const handleShinyToggle = (show: boolean): void => {
    setShowShiny(show);
    // Clear selected Pokémon when switching between shiny/non-shiny
    setSelectedPokemon(null);
  };

  const toggleLoop = (): void => {
    setIsLooping(!isLooping);
  };
  const mediaPlayerRef = useRef<MediaPlayerRef>(null);

  // Get the appropriate Pokémon list based on shiny toggle
  const availablePokemon = showShiny ? allShinyPokemon : allPokemon;

  // Load sample data on mount
  useEffect(() => {
    // Try to load LRC file first, fallback to TXT
    fetch('/sample/lyrics.lrc')
      .then(response => {
        if (response.ok) {
          return response.text();
        }
        throw new Error('LRC file not found');
      })
      .then(lrcContent => {
        console.log('LRC file loaded successfully');
        const parsedTimedLines = parseLrcFile(lrcContent);
        console.log('Parsed timed lines:', parsedTimedLines.length);
        setTimedLines(parsedTimedLines);
        setCurrentLyrics(lrcContent); // Set the raw LRC content for editing
      })
      .catch(() => {
        // Fallback to TXT file
        fetch('/sample/lyrics.txt')
          .then(response => response.text())
          .then(txtContent => {
            setCurrentLyrics(txtContent);
          })
          .catch(error => {
            console.error('Failed to load both LRC and TXT files:', error);
          });
      });

    const sampleLyrics = `Do you remember
The 21st night of September?
Love was changin' the minds of pretenders
While chasin' the clouds away
Our hearts were ringin'
In the key that our souls were singin'
As we danced in the night, remember
How the stars stole the night away, oh, yeah
Hey, hey, hey
Ba-dee-ya, say, do you remember?
Ba-dee-ya, dancin' in September
Ba-dee-ya, never was a cloudy day
Ba-du-da, ba-du-da, ba-du-da, ba-du
Ba-du-da, ba-du, ba-du-da, ba-du
Ba-du-da, ba-du, ba-du-da
My thoughts are with you
Holdin' hands with your heart to see you
Only blue talk and love, remember
How we knew love was here to stay
Now December
Found the love that we shared in September
Only blue talk and love, remember
The true love we share today
Hey, hey, hey
Ba-dee-ya, say, do you remember?
Ba-dee-ya, dancin' in September
Ba-dee-ya, never was a cloudy day
There was a
Ba-dee-ya (dee-ya, dee-ya), say, do you remember?
Ba-dee-ya (dee-ya, dee-ya), dancin' in September
Ba-dee-ya (dee-ya, dee-ya), golden dreams were shiny days
The bell was ringin', oh, oh
Our souls were singin'
Do you remember never a cloudy day? Yow
There was a
Ba-dee-ya (dee-ya, dee-ya), say, do you remember?
Ba-dee-ya (dee-ya, dee-ya), dancin' in September
Ba-dee-ya (dee-ya, dee-ya), never was a cloudy day
And we'll say
Ba-dee-ya (dee-ya, dee-ya), say, do you remember?
Ba-dee-ya (dee-ya, dee-ya), dancin' in September
Ba-dee-ya (dee-ya, dee-ya), golden dreams were shiny days
Ba-dee-ya, dee-ya, dee-ya
Ba-dee-ya, dee-ya, dee-ya
Ba-dee-ya, dee-ya, dee-ya, dee-ya!
Ba-dee-ya, dee-ya, dee-ya
Ba-dee-ya, dee-ya, dee-ya
Ba-dee-ya, dee-ya, dee-ya, dee-ya!`;

    setCurrentLyrics(sampleLyrics);

    // Load sample mappings
    const sampleMappings = [
      ["remember", "breloom"],
      ["21st night", "lunatone"],
      ["september", "magmar"],
      ["changin", "grovyle"],
      ["the minds", "dodrio"],
      ["pretenders", "charmander"],
      ["while", "zubat"],
      ["clouds away", "taillow"],
      ["our hearts", "chikorita"],
      ["ringin'", "bellsprout"],
      ["the key", "nosepass"],
      ["our souls", "dugtrio"],
      ["we danced", "hoppip"],
      ["the night", "noctowl"],
      ["hey, hey, hey", "nuzleaf"],
      ["ba-dee-ya", "ivysaur"],
      ["never", "spearow"],
      ["cloudy day", "pidgeotto"],
      ["ba-du-da", "swablu"],
      ["thoughts", "xatu"],
      ["your heart", "arcanine"],
      ["december", "combusken"],
      ["love that", "quilava"],
      ["we share", "omanyte"],
      ["today", "zapdos"],
      ["dee-ya", "vulpix"],
      ["dreams", "ponyta"],
      ["oh, oh", "pelipper"],
      ["yow", "marowak"],
      ["we'll say", "oddish"],
      ["?", "tangela"],
    ];

    const newMappings = new Map();
    sampleMappings.forEach(([text, pokemon]) => {
      newMappings.set(text.toLowerCase(), pokemon);
    });
    setMappings(newMappings);
  }, []);

  const addMapping = (text: string, pokemon: string): void => {
    const newMappings = new Map(mappings);
    const key = text.toLowerCase();
    newMappings.set(key, pokemon);
    setMappings(newMappings);
  };

  const removeMapping = (text: string): void => {
    const newMappings = new Map(mappings);
    
    // Try to delete the exact text first
    if (newMappings.has(text)) {
      newMappings.delete(text);
    } else {
      // If exact match not found, try case-insensitive match
      const lowerText = text.toLowerCase();
      for (const [key, value] of Array.from(newMappings.entries())) {
        if (key.toLowerCase() === lowerText) {
          newMappings.delete(key);
          break;
        }
      }
      
      // If still not found, try apostrophe normalization
      if (newMappings.size === mappings.size) {
        const textWithoutApostrophe = text.replace(/'/g, '');
        const lowerTextWithoutApostrophe = textWithoutApostrophe.toLowerCase();
        for (const [key, value] of Array.from(newMappings.entries())) {
          const keyWithoutApostrophe = key.replace(/'/g, '');
          if (keyWithoutApostrophe.toLowerCase() === lowerTextWithoutApostrophe) {
            newMappings.delete(key);
            break;
          }
        }
      }
      
      // If still not found, try punctuation normalization
      if (newMappings.size === mappings.size) {
        // Remove common punctuation and special characters from the text
        const textWithoutPunctuation = text.replace(/[.,!?;:()]/g, '').trim();
        const lowerTextWithoutPunctuation = textWithoutPunctuation.toLowerCase();
        for (const [key, value] of Array.from(newMappings.entries())) {
          if (key.toLowerCase() === lowerTextWithoutPunctuation) {
            newMappings.delete(key);
            break;
          }
        }
      }
    }
    
    setMappings(newMappings);
  };

  const clearAllMappings = (): void => {
    if (mappings.size === 0) return;
    if (
      window.confirm("Are you sure you want to clear all Pokémon mappings?")
    ) {
      setMappings(new Map());
    }
  };

  const filteredPokemon = availablePokemon.filter((pokemon) => 
    matchesPokemonSearch(pokemon, pokemonSearch)
  );

  // Calculate current line index for navigation
  const getCurrentLineIndex = (): number => {
    if (!timedLines || timedLines.length === 0) return -1;
    
    // Find the line that should be active based on current time
    for (let i = 0; i < timedLines.length; i++) {
      const currentLineTime = timedLines[i].time;
      const nextLineTime = i < timedLines.length - 1 ? timedLines[i + 1].time : Infinity;
      
      // Current line is active if time is >= current line time and < next line time
      // Add small tolerance for floating point precision issues
      const tolerance = 0.001;
      if (currentTime >= currentLineTime - tolerance && currentTime < nextLineTime - tolerance) {
        console.log(`App: Current time ${currentTime.toFixed(2)}s -> Line ${i + 1} (${currentLineTime.toFixed(2)}s - ${nextLineTime.toFixed(2)}s)`);
        return i;
      }
    }
    
    // If we're past all lines, return the last line
    console.log(`App: Current time ${currentTime.toFixed(2)}s -> Last line ${timedLines.length}`);
    return timedLines.length - 1;
  };

  const currentLineIndex = getCurrentLineIndex();

  return (
    <div className="app-container">
      <MobileBanner />
      <TopBar
        spriteSize={spriteSize}
        setSpriteSize={setSpriteSize}
        hidePokeballs={hidePokeballs}
        setHidePokeballs={setHidePokeballs}
        showRed={showRed}
        setShowRed={setShowRed}
        revealAllPokemon={revealAllPokemon}
        setRevealAllPokemon={setRevealAllPokemon}
      />

      <div className="main-content">
        <LeftSidebar
          pokemon={filteredPokemon}
          selectedPokemon={selectedPokemon}
          setSelectedPokemon={setSelectedPokemon}
          pokemonSearch={pokemonSearch}
          setPokemonSearch={setPokemonSearch}
          mappings={mappings}
          removeMapping={removeMapping}
          clearAllMappings={clearAllMappings}
          addMapping={addMapping}
          textInput={textInput}
          setTextInput={handleSetTextInput}
          showShiny={showShiny}
          setShowShiny={handleShinyToggle}
        />

        <PreviewArea
          lyrics={currentLyrics}
          mappings={mappings}
          spriteSize={spriteSize}
          hidePokeballs={hidePokeballs}
          showRed={showRed}
          caseInsensitive={true}
          removeMapping={removeMapping}
          addMapping={addMapping}
          selectedPokemon={selectedPokemon}
          setTextInput={handleSetTextInput}
          timedLines={timedLines}
          currentTime={currentTime}
          isPlaying={isPlaying}
          onSeekToTime={(time) => mediaPlayerRef.current?.seekToTime(time)}
          revealAllPokemon={revealAllPokemon}
        />

        <RightSidebar
          currentLyrics={currentLyrics}
          setCurrentLyrics={setCurrentLyrics}
          timedLines={timedLines}
          setTimedLines={setTimedLines}
          onSeekToTime={(time) => mediaPlayerRef.current?.seekToTime(time)}
          currentTime={currentTime}
          currentLineIndex={currentLineIndex}
        />
      </div>

      <MediaPlayer ref={mediaPlayerRef} onTimeUpdate={setCurrentTime} onPlayStateChange={setIsPlaying} isLooping={isLooping} onToggleLoop={toggleLoop} />
    </div>
  );
}

export default App;
